// «Tu negocio, en una pagina» (4.4, D4).
//
// El texto que el agente lee entero al empezar cada turno, antes del indice y
// antes de buscar en ningun archivo: que es el negocio, su vocabulario, sus
// reglas duras.
//
// **Se edita como texto, con el tope a la vista.** El tope son 1.500 palabras
// y no es un adorno: esto entra entero en cada turno, asi que lo que sobre se
// paga en todas las respuestas, no en la que lo escribio. Enseñar cuanto queda
// mientras se escribe es lo que evita descubrirlo al guardar.
//
// **Un solo escritor, y es su dueño.** El agente propone la primera version al
// ingerir y despues no la reescribe; si aprende algo que deberia estar aqui, lo
// propone como memoria y pasa por la puerta.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { workspacesService } from '@/services/workspacesService'

/** Las mismas que cuenta el servidor: lo que separa un espacio. Contar aqui de
 *  otra forma daria un numero que no coincide con el que decide al guardar. */
const palabrasDe = (texto: string) => texto.trim().split(/\s+/).filter(Boolean).length

export function BusinessContext() {
  const { t } = useTranslation()
  const [texto, setTexto] = useState('')
  const [tope, setTope] = useState(0)
  // **El aviso se guarda como clave y se traduce al pintar.** Guardarlo ya
  // traducido obliga a meter `t` en las dependencias del efecto, y `t` cambia
  // de identidad en cada render: el efecto volvia a pedir el texto sin parar y
  // pisaba lo que estabas escribiendo. Lo caza la prueba del tope.
  const [aviso, setAviso] = useState<'' | 'guardado' | 'error'>('')
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    let stillMounted = true
    workspacesService
      .contexto()
      .then((datos) => {
        if (!stillMounted) return
        setTexto(datos.texto)
        setTope(datos.tope)
      })
      .catch(() => stillMounted && setAviso('error'))
    return () => {
      stillMounted = false
    }
  }, [])

  const palabras = palabrasDe(texto)
  const sobra = tope > 0 && palabras > tope

  const save = async () => {
    setGuardando(true)
    setAviso('')
    try {
      const datos = await workspacesService.guardarContexto(texto)
      setTexto(datos.texto)
      setAviso('guardado')
    } catch {
      setAviso('error')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label id="contexto-etiqueta" htmlFor="contexto">
          {t('knowledge.contexto.titulo')}
        </Label>
        <p className="text-xs text-muted-foreground">
          {t('knowledge.contexto.explicacion')}
        </p>
      </div>

      <Textarea
        id="contexto"
        aria-labelledby="contexto-etiqueta"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder={t('knowledge.contexto.ejemplo')}
        rows={8}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* El tope a la vista mientras se escribe. En rojo solo cuando ya no
            cabe: un contador siempre rojo deja de significar nada. */}
        <p className={sobra ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
          {t('knowledge.contexto.cuenta', { palabras, tope })}
        </p>
        <Button size="sm" onClick={save} disabled={guardando || sobra}>
          {t('knowledge.contexto.guardar')}
        </Button>
      </div>

      {aviso && (
        <p className="text-sm text-muted-foreground">{t(`knowledge.contexto.${aviso}`)}</p>
      )}
    </div>
  )
}
